import type { ChessColor, PieceSymbol } from '@chessshooter/shared';
import { BOARD_COLORS, BOARD_STYLE } from '../uiConstants';

// The piece sprites: the twelve cburnett SVGs in public/pieces (see the README there), rasterised once
// per square size into offscreen canvases, because drawing an SVG image straight onto the canvas
// re-renders it every frame. Until an image has loaded, a letter in a disc stands in.

export type PieceCode = `${ChessColor}${'K' | 'Q' | 'R' | 'B' | 'N' | 'P'}`;

export const PIECE_CODES: readonly PieceCode[] = ['wK', 'wQ', 'wR', 'wB', 'wN', 'wP', 'bK', 'bQ', 'bR', 'bB', 'bN', 'bP'];

export function pieceCode(color: ChessColor, type: PieceSymbol): PieceCode {
  return `${color}${type.toUpperCase() as Uppercase<PieceSymbol>}`;
}

/** Where a piece's SVG is served; `base` is Vite's BASE_URL. */
export function pieceUrl(base: string, code: PieceCode): string {
  return `${base}pieces/${code}.svg`;
}

export interface PieceSprites {
  /** draws a piece with its top-left corner at (x, y), `size` CSS px wide; `dpr` picks the raster resolution */
  draw(ctx: CanvasRenderingContext2D, color: ChessColor, type: PieceSymbol, x: number, y: number, size: number, dpr: number): void;
  /** every SVG has loaded */
  ready(): boolean;
}

/** sizes only change on a resize, so a flush now and then beats bookkeeping */
const RASTER_CACHE_LIMIT = 48;

export function loadPieceSprites(base: string): PieceSprites {
  const images = new Map<PieceCode, HTMLImageElement>();
  const loaded = new Set<PieceCode>();
  for (const code of PIECE_CODES) {
    const img = new Image();
    img.decoding = 'async';
    img.addEventListener('load', () => loaded.add(code));
    img.src = pieceUrl(base, code);
    images.set(code, img);
  }
  const rasters = new Map<string, HTMLCanvasElement>();

  function raster(code: PieceCode, px: number): HTMLCanvasElement | null {
    const key = `${code}:${px}`;
    const hit = rasters.get(key);
    if (hit) return hit;
    const img = images.get(code);
    if (!img || !loaded.has(code)) return null;
    const canvas = document.createElement('canvas');
    canvas.width = px;
    canvas.height = px;
    const c = canvas.getContext('2d');
    if (!c) return null;
    c.drawImage(img, 0, 0, px, px);
    if (rasters.size >= RASTER_CACHE_LIMIT) rasters.clear();
    rasters.set(key, canvas);
    return canvas;
  }

  function fallback(ctx: CanvasRenderingContext2D, color: ChessColor, type: PieceSymbol, x: number, y: number, size: number): void {
    const cx = x + size / 2;
    const cy = y + size / 2;
    ctx.fillStyle = color === 'w' ? BOARD_COLORS.fallbackWhite : BOARD_COLORS.fallbackBlack;
    ctx.beginPath();
    ctx.arc(cx, cy, size * BOARD_STYLE.fallbackDisc, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = color === 'w' ? BOARD_COLORS.fallbackBlack : BOARD_COLORS.fallbackWhite;
    ctx.font = `700 ${Math.round(size * BOARD_STYLE.fallbackFont)}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(type.toUpperCase(), cx, cy);
  }

  return {
    draw(ctx, color, type, x, y, size, dpr) {
      const px = Math.max(1, Math.round(size * dpr));
      const sprite = raster(pieceCode(color, type), px);
      if (sprite) ctx.drawImage(sprite, x, y, size, size);
      else fallback(ctx, color, type, x, y, size);
    },
    ready: () => loaded.size === PIECE_CODES.length,
  };
}
